import type { TransformedCart, TransformedCartLine } from '~/server/utils/cartTypes'

export function useCart() {
  const cart = useState<TransformedCart | null>('cart:data', () => null)
  const cartId = useCookie<string | null>('shopify_cart_id', {
    default: () => null,
    maxAge: 60 * 60 * 24 * 30,
    sameSite: 'lax',
    path: '/',
  })

  const cartLines = computed<TransformedCartLine[]>(() => cart.value?.lines ?? [])
  const cartCount = computed(() => cartLines.value.reduce((sum, l) => sum + l.quantity, 0))
  const cartTotal = computed(() => cart.value?.totalAmount ?? '$0.00')
  const checkoutUrl = computed(() => cart.value?.checkoutUrl ?? null)

  async function fetchCart() {
    if (!cartId.value) return
    try {
      const data = await $fetch<TransformedCart>('/api/cart', {
        query: { id: cartId.value },
      })
      cart.value = data
    }
    catch {
      cartId.value = null
      cart.value = null
    }
  }

  async function addToCart(variantId: string, quantity = 1) {
    if (!cartId.value) {
      const data = await $fetch<TransformedCart>('/api/cart', {
        method: 'POST',
        body: { variantId, quantity },
      })
      cart.value = data
      cartId.value = data.id
    }
    else {
      const data = await $fetch<TransformedCart>('/api/cart/lines', {
        method: 'POST',
        body: { cartId: cartId.value, lines: [{ merchandiseId: variantId, quantity }] },
      })
      cart.value = data
    }
  }

  async function updateQuantity(lineId: string, quantity: number) {
    if (!cartId.value) return
    const data = await $fetch<TransformedCart>('/api/cart/lines', {
      method: 'PATCH',
      body: { cartId: cartId.value, lines: [{ id: lineId, quantity }] },
    })
    cart.value = data
  }

  async function removeItem(lineId: string) {
    if (!cartId.value) return
    const data = await $fetch<TransformedCart>('/api/cart/lines/remove', {
      method: 'POST',
      body: { cartId: cartId.value, lineIds: [lineId] },
    })
    cart.value = data
  }

  return { cartLines, cartCount, cartTotal, checkoutUrl, cartId, fetchCart, addToCart, updateQuantity, removeItem }
}
